import type { Ring, Vec2 } from '../../../geo/types';
import type { Frame, MeshBuilder } from '../../mesh/meshBuilder';
import { orientedFace, planPoint, subFrame } from '../solids';
import { gableRoof } from '../roofSolids';
import { CHURCH_PLAN, type ChapelPlan, type SidePlan, type TranseptPlan } from './churchModel';
import {
  buttress,
  centredWindow,
  gableWall,
  pointedWindows,
  rectWall,
  roofUv,
  slopedWall,
  type MasonryStyles,
} from './masonry';

export const SIDE_PARTS = {
  bay: 6,
  aisleSill: 2.2,
  aisleTopDrop: 0.8,
  hallSill: 3,
  hallTopDrop: 1.2,
  chapelSill: 2.2,
  chapelWindowReach: 0.55,
  chapelWindowShare: 0.5,
  transeptSill: 3,
  transeptWindowReach: 0.3,
  transeptWindowShare: 0.55,
  buttress: { width: 0.9, depth: CHURCH_PLAN.buttressDepth, heightDrop: 0.8, pinnacle: 0 },
  pinnacle: { width: 0.8, depth: CHURCH_PLAN.buttressDepth, rise: 0.6, pinnacle: 2.6 },
} as const;

interface Rect {
  readonly u0: number;
  readonly u1: number;
  readonly v0: number;
  readonly v1: number;
}

function rectOf(u0: number, u1: number, a: number, b: number): Rect {
  return { u0, u1, v0: Math.min(a, b), v1: Math.max(a, b) };
}

/** Counter-clockwise edges of a plan rectangle: south, east, north, west. */
function edges(r: Rect): { s: [Vec2, Vec2]; e: [Vec2, Vec2]; n: [Vec2, Vec2]; w: [Vec2, Vec2] } {
  const a: Vec2 = [r.u0, r.v0];
  const b: Vec2 = [r.u1, r.v0];
  const c: Vec2 = [r.u1, r.v1];
  const d: Vec2 = [r.u0, r.v1];
  return { s: [a, b], e: [b, c], n: [c, d], w: [d, a] };
}

/** Transverse roof: ridge along v over the middle of [u0, u1], gable walls supplied separately. */
function transverseGable(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  r: Rect,
  heights: { readonly eaveY: number; readonly ridgeY: number }
): void {
  const mid = (r.u0 + r.u1) / 2;
  const half = (r.u1 - r.u0) / 2;
  const turned = subFrame(frame, mid, 0, Math.PI / 2);
  const spec = {
    u0: r.v0,
    u1: r.v1,
    v0: -half,
    v1: half,
    eaveY: heights.eaveY,
    ridgeY: heights.ridgeY,
    ends: { start: false, end: false },
  };
  gableRoof(builder, styles, turned, spec);
}

export function buildLeanTo(builder: MeshBuilder, styles: MasonryStyles, frame: Frame, run: SidePlan): void {
  const r = rectOf(run.u0, run.u1, run.inner, run.outer);
  const e = edges(r);
  const north = run.side > 0;
  const outerEdge = north ? e.n : e.s;
  const len = run.u1 - run.u0;
  const openings = pointedWindows(len, SIDE_PARTS.bay, SIDE_PARTS.aisleSill, run.eaveY - SIDE_PARTS.aisleTopDrop);
  rectWall(builder, styles, frame, outerEdge, [0, run.eaveY], openings);
  rectWall(builder, styles, frame, north ? e.s : e.n, [0, run.topY]);
  slopedWall(builder, styles, frame, e.e, north ? run.topY : run.eaveY, north ? run.eaveY : run.topY);
  slopedWall(builder, styles, frame, e.w, north ? run.eaveY : run.topY, north ? run.topY : run.eaveY);
  const eave = (u: number) => planPoint(u, run.eaveY, run.outer);
  const top = (u: number) => planPoint(u, run.topY, run.inner);
  const points = [eave(run.u0), eave(run.u1), top(run.u1), top(run.u0)];
  orientedFace(builder, styles.roof, frame, points, [0, 1, -run.side], roofUv(points[0]!, points[1]!, points[2]!));
  sideButtresses(builder, styles, frame, run, run.eaveY - SIDE_PARTS.buttress.heightDrop);
}

function sideButtresses(builder: MeshBuilder, styles: MasonryStyles, frame: Frame, run: SidePlan, height: number): void {
  const count = Math.max(1, Math.round((run.u1 - run.u0) / SIDE_PARTS.bay));
  const step = (run.u1 - run.u0) / count;
  for (let k = 1; k < count; k++) {
    const at: Vec2 = [run.u0 + k * step, run.outer];
    buttress(builder, styles, frame, at, [0, run.side], { ...SIDE_PARTS.buttress, height });
  }
}

/** Side vessel of a hall church: full-height walls under its own gable roof. */
export function buildHallAisle(builder: MeshBuilder, styles: MasonryStyles, frame: Frame, run: SidePlan): void {
  const r = rectOf(run.u0, run.u1, run.inner, run.outer);
  const e = edges(r);
  const north = run.side > 0;
  const len = run.u1 - run.u0;
  const width = r.v1 - r.v0;
  const gable = { eaveY: run.eaveY, apexY: run.topY };
  const top = run.eaveY - SIDE_PARTS.hallTopDrop;
  const endTop = run.eaveY + 0.3 * (run.topY - run.eaveY);
  rectWall(builder, styles, frame, north ? e.n : e.s, [0, run.eaveY], pointedWindows(len, SIDE_PARTS.bay, SIDE_PARTS.hallSill, top));
  rectWall(builder, styles, frame, north ? e.s : e.n, [0, run.eaveY]);
  for (const edge of [e.e, e.w]) {
    gableWall(builder, styles, frame, edge, gable, centredWindow(width, SIDE_PARTS.hallSill, endTop, 0.5));
  }
  const spec = { u0: r.u0, u1: r.u1, v0: r.v0, v1: r.v1, eaveY: run.eaveY, ridgeY: run.topY };
  gableRoof(builder, styles, frame, { ...spec, ends: { start: false, end: false } });
}

/** A chapel under its transverse roof; `front` replaces the one window of its outer gable. */
export function buildChapel(builder: MeshBuilder, styles: MasonryStyles, frame: Frame, chapel: ChapelPlan, front?: readonly Ring[]): void {
  const r = rectOf(chapel.u0, chapel.u1, chapel.inner, chapel.outer);
  const e = edges(r);
  const north = chapel.side > 0;
  const width = chapel.u1 - chapel.u0;
  const gable = { eaveY: chapel.eaveY, apexY: chapel.ridgeY };
  const top = chapel.eaveY + SIDE_PARTS.chapelWindowReach * (chapel.ridgeY - chapel.eaveY);
  const window = front ?? centredWindow(width, SIDE_PARTS.chapelSill, top, SIDE_PARTS.chapelWindowShare);
  gableWall(builder, styles, frame, north ? e.n : e.s, gable, window);
  gableWall(builder, styles, frame, north ? e.s : e.n, gable);
  rectWall(builder, styles, frame, e.e, [0, chapel.eaveY]);
  rectWall(builder, styles, frame, e.w, [0, chapel.eaveY]);
  transverseGable(builder, styles, frame, r, chapel);
}

/** Buttresses crowned by stone pinnacles between the chapel gables of one row. */
export function buildChapelPinnacles(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  chapels: readonly ChapelPlan[]
): void {
  const seen = new Set<string>();
  const stone = { ...styles, roof: styles.wall };
  for (const c of chapels) {
    for (const u of [c.u0, c.u1]) {
      const key = `${c.side}:${u.toFixed(2)}:${c.outer.toFixed(2)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const spec = { ...SIDE_PARTS.pinnacle, height: c.eaveY + SIDE_PARTS.pinnacle.rise };
      buttress(builder, stone, frame, [u, c.outer], [0, c.side], spec);
    }
  }
}

/** Plan extent of the body beside a transept edge, beyond which the arm stands free. */
export interface BodyReach {
  readonly low: number;
  readonly high: number;
}

function transeptSideWall(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  t: TranseptPlan,
  u: number,
  body: BodyReach
): void {
  const west = u === t.u0;
  const cuts = [t.v0, Math.max(t.v0, body.low), Math.min(t.v1, body.high), t.v1];
  const points: Vec2[] = (west ? [...cuts].reverse() : cuts).map((v): Vec2 => [u, v]);
  for (let i = 0; i < 3; i++) {
    const from = points[i]!;
    const to = points[i + 1]!;
    if (Math.abs(to[1] - from[1]) < 1e-6) continue;
    const free = Math.abs(to[1] - from[1]);
    const roomy = i !== 1 && free >= SIDE_PARTS.bay / 2;
    const openings = roomy ? pointedWindows(free, SIDE_PARTS.bay, SIDE_PARTS.transeptSill, t.eaveY - 1.5) : [];
    rectWall(builder, styles, frame, [from, to], [0, t.eaveY], openings);
  }
}

export function buildTransept(
  builder: MeshBuilder,
  styles: MasonryStyles,
  frame: Frame,
  t: TranseptPlan,
  body: BodyReach
): void {
  const r = rectOf(t.u0, t.u1, t.v0, t.v1);
  const e = edges(r);
  const width = t.u1 - t.u0;
  const gable = { eaveY: t.eaveY, apexY: t.ridgeY };
  const top = t.eaveY + SIDE_PARTS.transeptWindowReach * (t.ridgeY - t.eaveY);
  for (const edge of [e.s, e.n]) {
    gableWall(builder, styles, frame, edge, gable, centredWindow(width, SIDE_PARTS.transeptSill, top, SIDE_PARTS.transeptWindowShare));
  }
  transeptSideWall(builder, styles, frame, t, t.u1, body);
  transeptSideWall(builder, styles, frame, t, t.u0, body);
  transverseGable(builder, styles, frame, r, t);
  const height = t.eaveY - SIDE_PARTS.buttress.heightDrop;
  for (const [u, v, side] of [
    [t.u0, t.v0, -1],
    [t.u1, t.v0, -1],
    [t.u0, t.v1, 1],
    [t.u1, t.v1, 1],
  ] as const) {
    buttress(builder, styles, frame, [u, v], [0, side], { ...SIDE_PARTS.buttress, height });
  }
}
